import React, { useRef, useState, useEffect } from "react";
import { Button } from "./ui/button";
import { Pen, Minus, ArrowUpRight, Square, Circle, Type, Undo2, Trash2 } from "lucide-react";

const W = 760, H = 460;
const TOOLS = [
  { k: "pen", Icon: Pen, label: "Pen" },
  { k: "line", Icon: Minus, label: "Line" },
  { k: "arrow", Icon: ArrowUpRight, label: "Arrow" },
  { k: "rect", Icon: Square, label: "Rectangle" },
  { k: "ellipse", Icon: Circle, label: "Ellipse" },
  { k: "text", Icon: Type, label: "Label" },
];
const COLORS = ["#0B132B", "#1E3A8A", "#DC2626", "#047857", "#F97316"];

function drawArrow(ctx, s) {
  const { x1, y1, x2, y2 } = s;
  ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
  const ang = Math.atan2(y2 - y1, x2 - x1);
  const len = 10 + s.width * 2;
  ctx.beginPath();
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - len * Math.cos(ang - Math.PI / 6), y2 - len * Math.sin(ang - Math.PI / 6));
  ctx.moveTo(x2, y2);
  ctx.lineTo(x2 - len * Math.cos(ang + Math.PI / 6), y2 - len * Math.sin(ang + Math.PI / 6));
  ctx.stroke();
}

export const SketchPad = ({ value, onChange }) => {
  const canvasRef = useRef(null);
  const bgRef = useRef(null);
  const shapesRef = useRef([]);
  const draftRef = useRef(null);
  const drawingRef = useRef(false);
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState("#0B132B");
  const [width, setWidth] = useState(2);

  const drawShape = (ctx, s) => {
    ctx.strokeStyle = s.color; ctx.fillStyle = s.color; ctx.lineWidth = s.width;
    ctx.lineCap = "round"; ctx.lineJoin = "round";
    if (s.type === "pen") { ctx.beginPath(); s.points.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y))); ctx.stroke(); }
    else if (s.type === "line") { ctx.beginPath(); ctx.moveTo(s.x1, s.y1); ctx.lineTo(s.x2, s.y2); ctx.stroke(); }
    else if (s.type === "arrow") { drawArrow(ctx, s); }
    else if (s.type === "rect") { ctx.strokeRect(s.x1, s.y1, s.x2 - s.x1, s.y2 - s.y1); }
    else if (s.type === "ellipse") { ctx.beginPath(); ctx.ellipse((s.x1 + s.x2) / 2, (s.y1 + s.y2) / 2, Math.abs(s.x2 - s.x1) / 2, Math.abs(s.y2 - s.y1) / 2, 0, 0, 2 * Math.PI); ctx.stroke(); }
    else if (s.type === "text") { ctx.font = `${s.size}px Helvetica, Arial, sans-serif`; ctx.textBaseline = "top"; ctx.fillText(s.text, s.x1, s.y1); }
  };

  const redraw = () => {
    const ctx = canvasRef.current.getContext("2d");
    ctx.clearRect(0, 0, W, H);
    ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, W, H);
    if (bgRef.current) ctx.drawImage(bgRef.current, 0, 0, W, H);
    shapesRef.current.forEach((s) => drawShape(ctx, s));
    if (draftRef.current) drawShape(ctx, draftRef.current);
  };

  const emit = () => {
    const empty = shapesRef.current.length === 0 && !bgRef.current;
    onChange(empty ? "" : canvasRef.current.toDataURL("image/png"));
  };
  const commit = () => { redraw(); emit(); };

  useEffect(() => {
    redraw();
    if (value && value.startsWith("data:image")) {
      const im = new window.Image();
      im.onload = () => { bgRef.current = im; redraw(); };
      im.src = value;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    return { x: (e.clientX - r.left) * (W / r.width), y: (e.clientY - r.top) * (H / r.height) };
  };

  const down = (e) => {
    e.preventDefault();
    const p = pos(e);
    if (tool === "text") {
      const t = window.prompt("Label text:");
      if (t && t.trim()) { shapesRef.current.push({ type: "text", x1: p.x, y1: p.y, text: t.trim(), color, size: 16, width }); commit(); }
      return;
    }
    drawingRef.current = true;
    draftRef.current = tool === "pen"
      ? { type: "pen", points: [p], color, width }
      : { type: tool, x1: p.x, y1: p.y, x2: p.x, y2: p.y, color, width };
    redraw();
  };
  const move = (e) => {
    if (!drawingRef.current || !draftRef.current) return;
    const p = pos(e); const d = draftRef.current;
    if (d.type === "pen") d.points.push(p); else { d.x2 = p.x; d.y2 = p.y; }
    redraw();
  };
  const up = () => {
    if (!drawingRef.current || !draftRef.current) { drawingRef.current = false; return; }
    drawingRef.current = false;
    const d = draftRef.current; draftRef.current = null;
    const tiny = d.type !== "pen" && Math.abs(d.x2 - d.x1) < 3 && Math.abs(d.y2 - d.y1) < 3;
    if (!tiny) shapesRef.current.push(d);
    commit();
  };
  const undo = () => { shapesRef.current.pop(); commit(); };
  const clearAll = () => { shapesRef.current = []; bgRef.current = null; commit(); };

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-3" data-testid="sketch-pad">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        {TOOLS.map(({ k, Icon, label }) => (
          <Button key={k} type="button" size="sm" variant={tool === k ? "default" : "outline"} title={label}
            className={tool === k ? "bg-[#1E3A8A] hover:bg-[#1D4ED8] h-8 px-2.5" : "h-8 px-2.5"}
            data-testid={`sketch-tool-${k}`} onClick={() => setTool(k)}><Icon className="h-4 w-4" /></Button>
        ))}
        <span className="mx-1 h-6 w-px bg-slate-300" />
        {COLORS.map((c) => (
          <button key={c} type="button" title={c} data-testid={`sketch-color-${c}`} onClick={() => setColor(c)}
            className={`h-6 w-6 rounded-full border-2 ${color === c ? "border-slate-800" : "border-white"}`} style={{ backgroundColor: c }} />
        ))}
        <span className="mx-1 h-6 w-px bg-slate-300" />
        <select value={width} onChange={(e) => setWidth(Number(e.target.value))} data-testid="sketch-width"
          className="h-8 rounded-md border border-slate-200 bg-white px-2 text-sm">
          <option value={1}>Thin</option><option value={2}>Medium</option><option value={4}>Thick</option><option value={6}>Bold</option>
        </select>
        <span className="flex-1" />
        <Button type="button" size="sm" variant="outline" className="h-8 px-2.5" title="Undo" data-testid="sketch-undo" onClick={undo}><Undo2 className="h-4 w-4" /></Button>
        <Button type="button" size="sm" variant="outline" className="h-8 px-2.5 text-red-600" title="Clear" data-testid="sketch-clear" onClick={clearAll}><Trash2 className="h-4 w-4" /></Button>
      </div>
      <canvas ref={canvasRef} width={W} height={H} data-testid="sketch-canvas"
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}
        className="w-full rounded-lg border border-slate-300 bg-white cursor-crosshair"
        style={{ touchAction: "none", aspectRatio: `${W} / ${H}` }} />
      <p className="text-xs text-slate-400 mt-1.5">Pick a tool, then draw on the canvas. Use the Label tool to click and add text. The sketch is saved with the letter and printed in the PDF.</p>
    </div>
  );
};
